/**
 * イベントバス。
 *
 * 公開イベント名は設計書のライフサイクル仕様で固定（発明禁止・coding.md）。
 * 内部イベント（engine:* / source:*）は型で分離し、公開 on()/off() からは見えない。
 * 公開バス（EP-6）は将来ここを包んで実装する。
 */
import type { EngineKind, FlipDirection } from './options';

/** 誌面ハイライト矩形（ページに対する 0–1 正規化座標）。 */
export interface SearchRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** 検索ヒット。page 以外はサーバ索引があるときのみ付く（加算拡張・M5）。 */
export interface SearchHit {
  page: number;
  /** 前後文脈つきスニペット（原文）。 */
  snippet?: string;
  /** スニペット文字列先頭を 0 とした一致範囲 [start, length]（強調表示用）。 */
  matchRanges?: readonly (readonly [number, number])[];
  /** 誌面ハイライトの矩形群（テキスト層座標由来）。 */
  rects?: readonly SearchRect[];
}

/** 公開イベント → ペイロードの対応。 */
export interface PublicEventMap {
  created: Record<string, never>;
  uiReady: Record<string, never>;
  ready: { pageCount: number };
  beforePageChange: { from: number; to: number };
  afterPageChange: { page: number };
  beforeFlip: { direction: FlipDirection };
  afterFlip: { page: number };
  resize: { width: number; height: number };
  searchDone: { query: string; hits: readonly SearchHit[] };
  error: { code: string; message: string; cause?: unknown };
  fallback: { from: EngineKind; to: EngineKind; reason: string };
}

/** エンジンが使えなくなった理由（公開 fallback イベントの reason になる）。 */
export type EngineFailureReason =
  /** WebGL 非対応（能力プローブが false） */
  | 'unsupported'
  /** レンダラ生成に失敗（コンテキストを作れなかった） */
  | 'context-create-failed'
  /** 実行中に GL コンテキストを失った */
  | 'context-lost'
  /** マウント中のその他の失敗 */
  | 'mount-failed';

/** 内部イベント（バンドル内部の連携専用）。 */
export interface InternalEventMap extends PublicEventMap {
  'engine:flipStart': { direction: FlipDirection };
  'engine:flipEnd': { page: number };
  /** エンジンが継続不能になった。core が 2D への降格と公開 fallback の発火を担う。 */
  'engine:lost': { reason: EngineFailureReason };
  'source:refreshed': { expiresAt: number };
  /**
   * 拡大率が変わった（EP-6 の計測点）。公開 11 種に zoom は無く名前は発明できないため
   * 内部イベントとして持つ。zoomTo が実際に値を変えたときだけ出る（上下限での連打は出ない）。
   */
  'viewer:zoom': { level: number; page: number };
  /**
   * 誌面オーバーレイが押された（EP-6 の計測点）。層のルートに張った 1 本の委譲リスナが
   * 拾うので、あとから登録された renderer の要素も自動的に対象になる。
   */
  'overlay:click': { id: number; type: string; page: number };
}

export type PublicEventName = keyof PublicEventMap;
export type EventHandler<T> = (payload: T) => void;

/** 公開イベントのハンドラ束（options.on / on() の双方で使う）。 */
export type EventHandlers = {
  [K in PublicEventName]: EventHandler<PublicEventMap[K]>;
};

/** 型付きイベントエミッタ。ハンドラの例外は握り、onHandlerError へ回す。 */
export class TypedEmitter<M extends object> {
  private readonly handlers = new Map<keyof M, Set<EventHandler<never>>>();

  constructor(private readonly onHandlerError?: (error: unknown, type: keyof M) => void) {}

  /** 購読する。戻り値は解除関数。 */
  on<K extends keyof M>(type: K, handler: EventHandler<M[K]>): () => void {
    let set = this.handlers.get(type);
    if (!set) {
      set = new Set();
      this.handlers.set(type, set);
    }
    set.add(handler as EventHandler<never>);
    return () => this.off(type, handler);
  }

  /** 購読を解除する。handler 省略でその型の全ハンドラを解除。 */
  off<K extends keyof M>(type: K, handler?: EventHandler<M[K]>): void {
    const set = this.handlers.get(type);
    if (!set) return;
    if (handler) {
      set.delete(handler as EventHandler<never>);
      if (set.size === 0) this.handlers.delete(type);
    } else {
      this.handlers.delete(type);
    }
  }

  /** 発火する。購読時点のスナップショットに対して呼ぶ（ハンドラ内の解除で崩れないため）。 */
  emit<K extends keyof M>(type: K, payload: M[K]): void {
    const set = this.handlers.get(type);
    if (!set) return;
    for (const handler of [...set]) {
      try {
        (handler as EventHandler<M[K]>)(payload);
      } catch (error) {
        this.onHandlerError?.(error, type);
      }
    }
  }

  /** 購読数（dispose 後の残留ゼロ検証で使う）。 */
  count(): number {
    let total = 0;
    for (const set of this.handlers.values()) total += set.size;
    return total;
  }

  dispose(): void {
    this.handlers.clear();
  }
}

/** 内部バス（1 ビューワにつき 1 個）。 */
export type InternalBus = TypedEmitter<InternalEventMap>;
